<script>
    // ===================== Imports =====================
    import { tick } from "svelte";
    import backend from "../server";
    import { ICONS } from "../js/icons";
    import ListCampaign from "../lib/components/modals/freebets/ListCampaign.svelte";
    import CreateCampaign from "../lib/components/modals/freebets/CreateCampaign.svelte";
    import PlayersCampaign from "../lib/components/modals/freebets/PlayersCampaign.svelte";
    import { genBonusId, addPlayersSequential, saveCampaignFlow } from "../lib/utils/freebetsFlow.js";
    import { makeEmptyCampaign, addToEnd as coreAddToEnd, mapRowToPlayersCampaign, toUnixSeconds, toInt, toNum, cleanUndefined } from "../lib/utils/freebetsCore.js";
    import { CampaignAPI } from "../lib/utils/freebetsApi.js";

    const FS = backend.freespin;

    // ===================================================
    // =================== AMIGOGAMING ===================
    // ===================================================

    // 1) Provider
    const PROVIDER_CODE = "agmg";
    // 2) Types allowed
    const AMIGO_TYPES   = { slot: true, live: false };
    // 3) Demo config (optional)
    const AMIGO_DEMO_CONFIG = {
        CODE: "JU02", ID: 44, DEMO_GAME: false,
        PLAYERS: [
            { id: "16", label: "carlosEUR",      currency: "EUR" },
            { id: "17", label: "amigogamingEUR", currency: "EUR" },
        ],
        CURRENCIES: ["EUR", "TEST"],
        DEFAULTS: { CURRENCY: "EUR" },
    };
    // 4) Bet scale's provider
    async function getAmigoBetScales({ clientCode, gameId, currency }) {
        const res   = await FS.getBetScalesAmigoGaming({
            clientCode: String(clientCode || ""),
            gameIDs:    String(gameId || ""),
            currencies: String(currency || "")
        });
        const list  = res?.data?.gameList?.[0]?.betScaleList ?? [];
        const entry = list.find(b => String(b?.currency).toUpperCase() === String(currency).toUpperCase());
        const raw   = Array.isArray(entry?.totalBetScales) ? entry.totalBetScales : [];
        return [...new Set(raw.map(Number).filter(n => Number.isFinite(n) && n > 0))].sort((a, b) => a - b);
    }
    // 5) Map to provider payloads
    // - addPlayer
    function mapAmigoAddPlayer({campaign, payload}) {
        const players = Array.isArray(payload) ? payload : [payload];
        const c = campaign || {};
        const slotCfgs = Array.isArray(c.slotConfigs) && c.slotConfigs.length > 0
            ? c.slotConfigs
            : (c.slotConfig?.games?.length
                ? c.slotConfig.games.map(g => ({
                    game: { gameId: g.gameId, gameName: g.gameName },
                    amountPerRound: c.slotConfig.amountPerRound,
                    rounds: c.slotConfig.rounds
                }))
                : []);
        const currency = (c?.currency ?? players[0]?.currency) || "";
        const startISO = c.startDate ?? c.startTime ?? players[0]?.startTime ?? null;
        const endISO = c.endDate ?? c.endTime ?? players[0]?.endTime ?? null;
        const clientCode = String(c?.client?.clientCode ?? c?.clientCode ?? "");
        
        const playersList = players.map(player => {
            const playerId = String(player.playerId || player.id || "");
            const bonusCode = String(player.bonusCode || "");
            
            return cleanUndefined({
                clientCode,
                bonusCode,
                playerId,
                startDate: toUnixSeconds(startISO),
                expirationDate: toUnixSeconds(endISO),
                rounds: toInt(slotCfgs[0]?.rounds || c?.rounds || players[0]?.rounds),
                gameList: slotCfgs.map(cfg => ({
                    gameId: cfg.game?.gameId || "",
                    betValues: [{currency, totalBet: toNum(cfg.amountPerRound || c?.amount || players[0]?.amount)}]
                }))
            });
        })
        return playersList;
        
    }
    // - removePlayer
    function mapAmigoRemovePlayer({ campaign, playerId, payload }) {
        const playerIdStr = String(playerId);
        const c = campaign || {};
        const bonusCode =
            payload?.bonusCode ??
            (Array.isArray(c?.playerList)
                ? c.playerList.find(p => String(p.playerId) === playerIdStr)?.bonusCode
                : undefined);
        const clientCode = String(
            c?.client?.clientCode ?? c?.clientCode ?? c?.client?.clientId ?? c?.clientId ?? ""
        );
        return cleanUndefined({
            clientCode,
            playerId: playerIdStr,
            bonusCode,
            operatorReference: c?.operatorReference ?? c?.bonusSeed ?? c?.campaignId
        });
    }
    // 5) Integration props
    const AMIGO_INTEGRATION = {
        provider: PROVIDER_CODE,
        providerName: "Amigo Gaming",
        maxDays: 30,
        validateClient: FS.validateClientAmigoGaming,
        getBetScales: getAmigoBetScales,
        demo: AMIGO_DEMO_CONFIG,
        bonusPrefix: "AGMG_",
        bonusCodeLen: 8
    };
    // 6) Provider api (single place)
    const PROV_API = {
        savePlayer:   (dto) => FS.saveAmigoGaming(dto),
        deletePlayer: (dto) => FS.deleteAmigoGaming(dto)
    };

    // ===================================================
    // ========== Status and UI ==========================
    // ===================================================

    let isNew = false;
    let campaign = makeEmptyCampaign();
    let selectedClient = "";
    let campaignType = "";
    let selectionMode = "none";
    $: selectionMode = campaignType === "SLOT" ? "single" : campaignType === "LIVE" ? "multiple" : "none";
    let creationFlow = null;
    let isSaving = false;
    let modalVersion = 0;
    let lastPayload = null;
    let showPlayersCampaign = false;
    let playersCampaign = null;

    function created() {
        campaign = makeEmptyCampaign();
        isNew = true;
        campaignType = "";
        selectedClient = "";
        modalVersion++;
    }
    const closeCreate  = () => (isNew = false);
    const addToEnd     = (delta) => { campaign = coreAddToEnd(campaign, delta); };
    const closePlayersCampaign = () => (showPlayersCampaign = false);
    function openPlayersCampaign(row) {
        playersCampaign = mapRowToPlayersCampaign(row, { provider: PROVIDER_CODE, bonusPrefix: AMIGO_INTEGRATION.bonusPrefix });
        showPlayersCampaign = true;
    }

    // ===================================================
    // ============ Provider actions + API ===============
    // ===================================================

    const amigoActions = {
        createCampaignDoc: (doc) => CampaignAPI.createCampaignIgp(doc),
        preflightProviderPlayers: async ({players, campaign, genBonusId, bonusPrefix}) => {
            const ok = (players || []).map(it => ({
                id: String(it.id ?? it.playerId),
                bonusCode: genBonusId(bonusPrefix || "", 12)
            }));
            // Provider call happens in addPlayer with campaignId
            return {playersWithBonus: ok};
        },
        addPlayer: async (campaignId, payload) => {
            const players = Array.isArray(payload) ? payload : [payload];
            const clean = players.map(p => {
                const { __skipProvider, ...rest } = p;
                return rest;
            });
            let campaignDoc;
            try {
                campaignDoc = await CampaignAPI.getCampaignByCampaignIdIgp(campaignId);
            } catch (fetchErr) {
                console.error("Error fetching campaign before provider call:", fetchErr);
                throw new Error("No se pudo cargar la campaña para llamar al proveedor.");
            }
            // Save to DB first (player must exist before recording deliveries)
            let dbResult;
            try {
                dbResult = await CampaignAPI.addPlayerToCampaignIgp(campaignId, clean);
            } catch (dbErr) {
                throw dbErr;
            }

            // Then call provider with the fresh campaign document
            try {
                const providerPayload = mapAmigoAddPlayer({
                    campaign: campaignDoc || playersCampaign || lastPayload,
                    payload: clean
                });
                const withCid = providerPayload.map(p => ({...p, campaignId}));
                await PROV_API.savePlayer(withCid);
            } catch (provErr) {
                // Provider failed; remove players from DB to keep state consistent
                for (const player of clean) {
                    try {
                        await CampaignAPI.updatePlayerStatus(player.bonusCode, 0);
                    } catch {}
                }
                throw provErr;
            }

            return dbResult;
        },
        removePlayer: async (campaignId, playerOrObj, promo) => {
            const playerId = typeof playerOrObj === "object"
                ? String(playerOrObj.playerId || "")
                : String(playerOrObj || "");
            if (!playerId) throw new Error("playerId requerido");

            if (promo?.bonusCode) {
                let campaignDoc;
                try {
                    campaignDoc = await CampaignAPI.getCampaignByCampaignIdIgp(campaignId);
                } catch (fetchErr) {
                    console.error("Error fetching campaign before cancel:", fetchErr);
                }
                const cancelPayload = mapAmigoRemovePlayer({
                    campaign: campaignDoc || playersCampaign || lastPayload,
                    playerId,
                    payload: { ...playerOrObj, bonusCode: promo.bonusCode }
                });
                if (!cancelPayload.bonusCode) {
                    throw new Error("No se pudo resolver el bonusCode del bono a cancelar.");
                }
                return FS.deleteAmigoGaming(cancelPayload);
            }

            return FS.cancelPlayerAmigoGaming(campaignId, playerId);
        }
    };
    let listRef;

    async function saveCampaign() {
        if (!lastPayload) { alert("No hay datos para guardar."); return; }
        try {
            isSaving = true;
            creationFlow = { stage: "creating", total: 0, added: 0, errors: 0 };
            await tick();
            console.log("🔥 saveCampaign called!", performance.now());
            await saveCampaignFlow({
                actions: amigoActions,
                payload: lastPayload,
                genBonusId,
                bonusPrefix: AMIGO_INTEGRATION.bonusPrefix,
                addPlayersSequential,
                onStage: (stage, info) => {
                    const { done = 0, total = 0, errors = 0 } = info || {};
                    creationFlow = { stage, total, added: done, errors };
                }
            });
            await listRef?.refresh?.();
            closeCreate();
        } catch (e) {
            console.error(e);
            alert("Error creando campaña");
        } finally {
            isSaving = false;
            setTimeout(() => (creationFlow = null), 300);
        }
    }

    // API Campaign
    const api = {
        listByProvider: CampaignAPI.listByProviderIgp,
        cancelCampaign: CampaignAPI.cancelCampaignIgp,
        getById:        CampaignAPI.getCampaignByCampaignIdIgp,
        updateStatus:   CampaignAPI.updateCampaignStatusIgp,
        updateStatusPlayer: (bonusOrId, status /*, campaignRow */) =>
            CampaignAPI.updatePlayerStatus(bonusOrId, status),
        providerDeletePlayer: ({ campaign, player }) => {
            const campaignId = campaign?.campaignId;
            const playerId = String(player?.playerId || "");
            if (!campaignId || !playerId) throw new Error("campaignId y playerId requeridos");
            return FS.cancelPlayerAmigoGaming(campaignId, playerId);
        }
    };
</script>

<ListCampaign
        bind:this={listRef}
        provider={PROVIDER_CODE}
        {api}
        icons={ICONS}
        autoReconcileOnLoad={false}
        on:create={created}
        on:openplayers={async (e) => {
    const id = e.detail?.campaignId;
    let fresh = null;
    try { fresh = await listRef?.reconcileOne?.(id); } catch {}
    openPlayersCampaign(fresh || e.detail);
  }}
/>

{#if isNew}
    {#key modalVersion}
        <CreateCampaign
                {isNew}
                bind:campaign
                bind:campaignType
                bind:selectedClient
                manageClients="self"
                {selectionMode}
                integration={AMIGO_INTEGRATION}
                enabledTypes={AMIGO_TYPES}
                saving={isSaving}
                {creationFlow}
                on:submit={(e) => { lastPayload = e.detail; }}
                on:close={closeCreate}
                on:save={saveCampaign}
                on:addtoend={(e) => addToEnd(e.detail)}
        />
    {/key}
{/if}

{#if showPlayersCampaign}
    <PlayersCampaign
            campaign={playersCampaign}
            actions={amigoActions}
            getCampaignByCampaignId={CampaignAPI.getCampaignByCampaignIdIgp}
            autoRefetchOnOpen={false}
            demoConfig={AMIGO_DEMO_CONFIG}
            on:close={() => (showPlayersCampaign = false)}
            on:playersUpdated={(e) => {
      const { campaignId, playerList } = e.detail || {};
      listRef?.updateRowPlayers?.(campaignId, playerList);
    }}
    />
{/if}
